import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PEOPLE_SHARDS, commonsPage, fetchPeopleShard } from "../library/castPhotos.js";

/**
 * Settings → About → Image credits (roadmap Section 26 step 7): every cast photo the app can
 * show, with its photographer, licence and Commons page, as CC BY and CC BY-SA ask ("in any
 * reasonable manner"; a blanket line naming nobody would not do). The photos are listed a
 * people file at a time, Previous / Next between them; each line is a stop, so the remote
 * scrolls the list.
 */
export function ImageCreditsPage() {
  const [shard, setShard] = useState(0);
  const nextRef = useRef<HTMLButtonElement>(null);
  const page = useQuery({
    queryKey: ["peopleShard", shard],
    queryFn: () => fetchPeopleShard(shard),
    staleTime: 24 * 60 * 60 * 1000,
  });
  useEffect(() => nextRef.current?.focus(), []);
  const people = Object.values(page.data ?? {}).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <div className="page page--about">
      <div className="settings-card">
        <h1 className="settings-title">Image credits</h1>
        <p className="settings-row-hint">
          Cast photos come from Wikimedia Commons. Each is shown under its own licence; the
          photographer, the licence and the photo's page are listed here.
        </p>
        <div className="image-credits__nav">
          <button type="button" className="ui-btn" disabled={shard === 0} onClick={() => setShard(Math.max(0, shard - 1))}>◀ Previous</button>
          <span className="settings-row-hint">Page {shard + 1} of {PEOPLE_SHARDS}</span>
          <button type="button" ref={nextRef} className="ui-btn" disabled={shard === PEOPLE_SHARDS - 1} onClick={() => setShard(Math.min(PEOPLE_SHARDS - 1, shard + 1))}>Next ▶</button>
        </div>
        {page.isLoading && <p className="settings-row-hint">Loading…</p>}
        {page.isError && <p className="settings-row-hint">The credits could not be loaded. Try again later.</p>}
        {!page.isLoading && !page.isError && people.length === 0 && <p className="settings-row-hint">No photos on this page.</p>}
        <ul className="image-credits__list">
          {people.map(([name, , artist, license, licenseUrl, file]) => (
            <li key={file + name} className="image-credits__row" tabIndex={0} role="group" aria-label={`${name}: photo by ${artist}, ${license}`}>
              <strong>{name}</strong>
              <span>Photo: {artist} · {license}{licenseUrl ? ` (${licenseUrl})` : ""}</span>
              <small>{commonsPage(file)}</small>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
